import { useCallback, useRef } from "react";
import type React from "react";

type Direction = "first" | "last";

export default function useWorkspaceMenuFocusNavigation() {
    const focusableItemRefs = useRef<Array<HTMLElement | null>>([]);

    const setFocusableItemRef = useCallback((index: number) => (element: HTMLElement | null) => {
        focusableItemRefs.current[index] = element;
    }, []);

    const getFocusableItems = useCallback(() => {
        return focusableItemRefs.current.filter(
            (item): item is HTMLElement => Boolean(item) && document.contains(item)
        );
    }, []);

    const focusWorkspaceItem = useCallback((direction: Direction) => {
        const items = getFocusableItems();
        if (items.length === 0) return;
        if (direction === "first") items[0].focus();
        else items[items.length - 1].focus();
    }, [getFocusableItems]);

    const handleSearchKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
        const key = e.key;
        e.stopPropagation();
        if (key === "ArrowDown" || key === "ArrowUp") {
            e.preventDefault();
            if (key === "ArrowUp") focusWorkspaceItem("last");
            else focusWorkspaceItem("first");
        }
    }, [focusWorkspaceItem]);

    const handleWorkspaceListKeyDownCapture = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
        const key = e.key;
        if (key !== "ArrowDown" && key !== "ArrowUp") return;
        e.preventDefault();
        e.stopPropagation();

        const items = getFocusableItems();
        if (items.length === 0) return;

        const active = document.activeElement as HTMLElement | null;
        const index = active ? items.indexOf(active) : -1;

        let nextIndex = index;
        if (key === "ArrowDown") nextIndex = index < items.length - 1 ? index + 1 : 0;
        if (key === "ArrowUp") nextIndex = index > 0 ? index - 1 : items.length - 1;

        items[nextIndex].focus();
    }, [getFocusableItems]);

    return {
        setFocusableItemRef,
        handleSearchKeyDown,
        handleWorkspaceListKeyDownCapture,
    };
}
